<template>
  <ui-component-modal
    modalTitle=""
    :isSaving="isSaving"
    :isSavingSuccess="isSavingSuccess"
    :isSavingError="isSavingError"
    :hideFooterButtons="isSavingError || isSavingSuccess"
    :onClickCancel="onClickCancel"
    :onClickSave="updateCustomer"
    :showModal="showModal"
    @closeModal="onClickCancel"
  >
    <template slot="modalTitle"></template>
    <template v-slot:content>
      <div
        v-if="mCustomer"
        class="has-padding-bottom"
      >
        <BaseInputField
          v-model="mCustomer.FirstName"
          :label="$t(`Components.Customers.CreateCustomer.Label_FirstName`)"
          :labelForInput="labelForInput"
          :error="firstNameErrors.toString()"
          @input="delayTouch($v.mCustomer.FirstName)"
          @blur="$v.mCustomer.FirstName.$touch()"
          required
        />
        <BaseInputField
          v-model="mCustomer.LastName"
          :label="$t(`Components.Customers.CreateCustomer.Label_LastName`)"
          :labelForInput="labelForInput"
          :error="lastNameErrors.toString()"
          @input="$v.mCustomer.LastName.$touch()"
          @blur="$v.mCustomer.LastName.$touch()"
          required
        />

        <BaseInputField
          v-model="mCustomer.Email"
          :label="$t(`Components.Customers.CreateCustomer.Label_Email`)"
          :labelForInput="labelForInput"
          :error="emailErrors.toString()"
          @change="delay(checkCustomerEmail, 200)"
          @input="
            () => {
              emailAlreadyExists = false
              delayTouch($v.mCustomer.Email)
            }
          "
          @blur="$v.mCustomer.Email.$touch()"
          required
        />

        <BaseInputField
          v-model="mCustomer.Phone"
          :label="$t(`Components.Customers.CreateCustomer.Label_Phone`)"
          :labelForInput="labelForInput"
          error=""
          :required="false"
        />

        <hr />

        <BaseInputField
          v-model="mCustomer.Address"
          :label="$t(`Components.Customers.CreateCustomer.Label_Address`)"
          :labelForInput="labelForInput"
          error=""
          :required="false"
        />

        <BaseInputField
          v-model="mCustomer.PostalCode"
          :label="$t(`Components.Customers.CreateCustomer.Label_Postalcode`)"
          :labelForInput="labelForInput"
          error=""
          :required="false"
        />
        <BaseInputField
          v-model="mCustomer.City"
          :label="$t(`Components.Customers.CreateCustomer.Label_City`)"
          :labelForInput="labelForInput"
          error=""
          :required="false"
        />
        <BaseInputField
          v-model="mCustomer.State"
          :label="$t(`Components.Customers.CreateCustomer.Label_State`)"
          :labelForInput="labelForInput"
          error=""
          :required="false"
        />
        <BaseInputField
          v-model="mCustomer.Country"
          :label="$t(`Components.Customers.CreateCustomer.Label_Country`)"
          :labelForInput="labelForInput"
          error=""
          :required="false"
        />
      </div>
    </template>
  </ui-component-modal>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import customerProvider from '@/providers/customer'
import { required, email, minLength } from 'vuelidate/lib/validators'
const touchMap = new WeakMap()

export default {
  components: {
    BaseInputField: () => import('@/components/UI/Form/BaseInputField'),
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },

    onClickCancel: {
      type: Function,
      required: true,
    },

    labelForInput: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isSaving: false,
      isSavingSuccess: false,
      isSavingError: false,
      emailAlreadyExists: false,
      mCustomer: null,
    }
  },

  /**
   * Form validation rules
   */
  validations() {
    let output = {
      FirstName: {
        required,
        minLength: minLength(2),
      },
      LastName: {
        required,
        minLength: minLength(2),
      },
      Email: {
        required,
        email,
      },
    }

    return {
      mCustomer: output,
    }
  },

  computed: {
    ...mapState('customerStore', ['customer']),
    ...mapState('locationStore', ['locationState']),

    firstNameErrors() {
      const errors = []
      if (!this.$v.mCustomer.FirstName.$dirty) return errors
      !this.$v.mCustomer.FirstName.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      !this.$v.mCustomer.FirstName.minLength &&
        errors.push(this.$t('Form.InputErrors.minLength', { minLength: 2 }))
      return errors
    },
    lastNameErrors() {
      const errors = []
      if (!this.$v.mCustomer.LastName.$dirty) return errors
      !this.$v.mCustomer.LastName.required &&
        errors.push(this.$t('Form.InputErrors.Required'))
      return errors
    },
    emailErrors() {
      const errors = []
      if (!this.$v.mCustomer.Email.$dirty) return errors
      if (!this.$v.mCustomer.Email.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      } else if (!this.$v.mCustomer.Email.email) {
        errors.push(this.$t('Form.InputErrors.InvalidEmail'))
      } else if (this.emailAlreadyExists) {
        errors.push(this.$t('Form.InputErrors.EmailExists'))
      }
      return errors
    },
  },

  created() {
    this.mCustomer = JSON.parse(JSON.stringify(this.customer))
  },

  methods: {
    ...mapMutations('customerStore', ['setCustomer']),

    delayTouch($v) {
      $v.$reset()
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v))
      }
      touchMap.set($v, setTimeout($v.$touch, 1000))
    },

    checkCustomerEmail() {
      if (this.customer.Email !== this.mCustomer.Email) {
        customerProvider.methods
          .checkIfCustomerExists(this.locationState.Id, this.mCustomer.Email)
          .then((response) => {
            if (response.status === 200) {
              this.emailAlreadyExists = response.data
            }
          })
          .catch((err) => { })
          .finally(() => { })
      }
    },

    delay(callback, ms) {
      let self = this

      if (self.timer) {
        clearTimeout(self.timer)
      }
      this.timer = setTimeout(() => {
        callback.apply(self)
        clearTimeout(self.timer)
      }, ms || 0)
    },

    async updateCustomer() {
      let self = this
      this.errorMessage = ''
      this.emailAlreadyExists = false
      if (!this.isSaving) {
        this.isSaving = true
        this.$v.$touch()
        if (this.$v.$invalid) {
          this.isSaving = false
        } else {
          await customerProvider.methods
            .updateCustomer(this.mCustomer)
            .then((response) => {
              if (response.status === 200) {
                self.setCustomer(response.data)
                self.onClickCancel()
              }
            })
            .catch((e) => {
              if (e.response && e.response.status === 403) {
                this.emailAlreadyExists = true
              }
              self.errorMessage = self.$t(
                'Components.AccountControls.Register.Message_ErrorCode500'
              )
              self.isSaving = false
            })
        }
      }
    },
  },
}
</script>
